import { defineMessages } from "../define";

/**
 * Developer page "Catalog": the customer's recipes, ingredients and products in its database
 * (pizza-backend /api/admin/catalog), with their editors and the history of imports and edits.
 */
export default defineMessages({
  en: {
    "catalog.title": "Catalog",
    "catalog.description":
      "Recipes, ingredients and products the evaluation checks pizzas against, kept in this customer's database. A saved change applies to the next evaluation; pizzas already evaluated keep their verdict.",
    "catalog.forbidden": "The catalog is available to the developer role only.",
    "catalog.reload": "Reload",
    "catalog.lastImport": "Last package import by {who}",
    "catalog.neverImported": "No package imported yet.",
    "catalog.sourceFiles":
      "This backend evaluates with a catalog package from the repository (catalog_source=files). Changes made here are saved to the database, but evaluations use them only with catalog_source=db.",
    "catalog.empty":
      "The catalog is empty. Import the customer's package first: tools/catalog/prod_catalog.sh import <customer>.",
    "catalog.loading": "Loading…",
    "catalog.error.unknown": "Unknown error",

    "catalog.tab.recipes": "Recipes",
    "catalog.tab.ingredients": "Ingredients",
    "catalog.tab.products": "Products",
    "catalog.tab.history": "History",

    "catalog.chip.detectable": "Checked on the photo: the detector sees it as {classes}",
    "catalog.chip.notDetectable": "Not checked: the detector cannot see it",
    "catalog.chip.afterBaking": "after baking",

    "catalog.save.conflict": "Somebody changed it in the meantime ({error}). Load the current state, check your change and save again.",
    "catalog.save.invalid": "The change would make the catalog inconsistent:",
    "catalog.save.failed": "Saving failed: {error}",
    "catalog.save.reload": "Load current",
    "catalog.save.cancel": "Cancel",
    "catalog.save.saving": "Saving…",
    "catalog.save.save": "Save",

    "catalog.tags.remove": "Remove {value}",
    "catalog.tags.add": "Add",

    "catalog.picker.search": "Search ingredients…",
    "catalog.picker.none": "No ingredient matches.",
    "catalog.picker.taken": "in the recipe",
    "catalog.picker.notDetectable": "not checked",

    "catalog.list.changedBy": "changed by {who}",
    "catalog.list.empty": "Nothing matches.",
    "catalog.legend.checkedSample": "ham",
    "catalog.legend.checked": "checked on the photo",
    "catalog.legend.notCheckedSample": "mozzarella",
    "catalog.legend.notChecked": "not checked (the detector cannot see it)",

    // recipes
    "catalog.recipes.search": "Search by name, id or ingredient…",
    "catalog.recipes.add": "New recipe",
    "catalog.recipes.checked.one": "{count} of {total} checked",
    "catalog.recipes.checked.few": "{count} of {total} checked",
    "catalog.recipes.checked.other": "{count} of {total} checked",
    "catalog.recipe.newTitle": "New recipe",
    "catalog.recipe.editTitle": "Recipe {name}",
    "catalog.recipe.newDescription":
      "The id is how the evaluation finds the recipe: for Vosime the menu number in the order item's name (\"40. Spooky Pizza\" → 40).",
    "catalog.recipe.editDescription": "Id {id}, version {version}. Saving creates a new version.",
    "catalog.recipe.name": "Name",
    "catalog.recipe.id": "Id",
    "catalog.recipe.idMissing": "Fill in the id.",
    "catalog.recipe.idInvalid": "Letters, digits, '-' or '_' only.",
    "catalog.recipe.idTaken": "This id is taken.",
    "catalog.recipe.components": "Ingredients",
    "catalog.recipe.afterBakingColumn": "After baking",
    "catalog.recipe.pickIngredient": "Pick an ingredient",
    "catalog.recipe.afterBaking": "after baking",
    "catalog.recipe.removeComponent": "Remove the ingredient",
    "catalog.recipe.addComponent": "Add an ingredient",
    "catalog.recipe.duplicate": "An ingredient is listed twice.",
    "catalog.recipe.detectableHint":
      "Only ingredients with the eye are checked on the photo (the detector sees them); the others are listed for completeness. \"After baking\" marks what goes onto the baked pizza.",
    "catalog.recipe.appliesHint": "Applies to the next evaluation.",
    "catalog.recipe.extraIgnore": "Never shown as extra ingredients",
    "catalog.recipe.extraIgnoreAdd": "Add a look-alike…",
    "catalog.recipe.extraIgnoreHint":
      "What the detector takes one of this pizza's own ingredients for (Spooky: charcoal cheddar looks like jalapeño). The pizza never shows these as extra ingredients.",
    "catalog.recipe.extraIgnoreChanged": "never shown as extra: {names}",
    "catalog.recipe.saved": "{name} saved (version {version})",
    "catalog.recipe.versions.one": "{count} version",
    "catalog.recipe.versions.few": "{count} versions",
    "catalog.recipe.versions.other": "{count} versions",
    "catalog.recipe.version": "v{version}",
    "catalog.recipe.validFrom": "from {at}",
    "catalog.recipe.validAlways": "from the start",
    "catalog.recipe.renamed": "renamed {from} → {to}",
    "catalog.recipe.orderOnly": "order of the ingredients changed",

    // ingredients
    "catalog.ingredients.search": "Search by name, id or class…",
    "catalog.ingredients.add": "New ingredient",
    "catalog.ingredients.filter": "Show",
    "catalog.ingredients.filter.all": "All",
    "catalog.ingredients.filter.checked": "Checked",
    "catalog.ingredients.filter.unchecked": "Not checked",
    "catalog.ingredient.newTitle": "New ingredient",
    "catalog.ingredient.editTitle": "Ingredient {name}",
    "catalog.ingredient.newDescription": "Something recipes and orders name; checked on the photo when it has detector classes.",
    "catalog.ingredient.usage.one": "Used by {count} recipe",
    "catalog.ingredient.usage.few": "Used by {count} recipes",
    "catalog.ingredient.usage.other": "Used by {count} recipes",
    "catalog.ingredient.name": "Name",
    "catalog.ingredient.nameHint": "The name the evaluation reports, e.g. as a missing ingredient.",
    "catalog.ingredient.id": "Id",
    "catalog.ingredient.idMissing": "Fill in the id.",
    "catalog.ingredient.idInvalid": "Lowercase letters and digits separated by '-'.",
    "catalog.ingredient.idTaken": "This id is taken.",
    "catalog.ingredient.aliases": "Names in recipes and orders",
    "catalog.ingredient.aliasesHint":
      "Every name the ingredient goes by, its own name and the till's extras included. Case and diacritics do not matter; a name belongs to one ingredient only.",
    "catalog.ingredient.aliasPlaceholder": "Add a name, Enter",
    "catalog.ingredient.aliasTaken": "\"{alias}\" is already a name of {owner}.",
    "catalog.ingredient.classes": "Detector classes",
    "catalog.ingredient.classesHint": "Classes of the component detector that see this ingredient. None = not checked on the photo.",
    "catalog.ingredient.classPlaceholder": "Add a class, Enter",
    "catalog.ingredient.unknownClasses":
      "No other ingredient uses {classes}. Make sure the detector knows the class, otherwise the ingredient is always reported missing.",
    "catalog.ingredient.newHint": "A new ingredient is looked up after the existing ones.",
    "catalog.ingredient.extraMinCount": "Pieces to show as an extra ingredient",
    "catalog.ingredient.extraMinCountHint":
      "Confident pieces the detector must find on a pizza without this ingredient to show it as extra. Empty = the setting of the Evaluation page. Fewer for big pieces (camembert: 2).",
    "catalog.ingredient.extraMinCountInvalid": "A whole number from 1 to 50.",
    "catalog.ingredient.extraMinShare": "Share of the pizza to show as an extra sauce",
    "catalog.ingredient.extraMinShareHint":
      "The sauce shows as extra when it covers at least this share of a pizza without it. Empty = any amount the detector reports.",
    "catalog.ingredient.extraMinShareInvalid": "From 0 to 100 %.",
    "catalog.ingredient.extraDefault": "setting",
    "catalog.ingredient.extraAny": "any",
    "catalog.ingredient.notChecked": "not checked",
    "catalog.ingredient.alsoCalled": "also: {names}",
    "catalog.ingredient.saved": "{name} saved",

    // products
    "catalog.products.note":
      "Evaluations do not use products yet: Vosime's recipe comes from the number in the order item's name. Products will map the items of other customers' tills to recipes.",
    "catalog.products.search": "Search by name, id or recipe…",
    "catalog.products.add": "New product",
    "catalog.products.checkedAs": "checked as {recipe}",
    "catalog.product.newTitle": "New product",
    "catalog.product.editTitle": "Product {name}",
    "catalog.product.description": "What the kitchen sells; a pizza names the recipe it is checked against.",
    "catalog.product.name": "Name",
    "catalog.product.id": "Id",
    "catalog.product.idMissing": "Fill in the id.",
    "catalog.product.idInvalid": "Letters, digits, '-' or '_' only.",
    "catalog.product.idTaken": "This id is taken.",
    "catalog.product.kind": "Kind",
    "catalog.product.kind.pizza": "Pizza",
    "catalog.product.kind.other": "Other",
    "catalog.product.recipe": "Recipe",
    "catalog.product.recipeMissing": "A pizza needs a recipe.",
    "catalog.product.noRecipe": "No recipe",
    "catalog.product.saved": "{name} saved",

    // history
    "catalog.history.note":
      "Imports of the customer's package and edits made here. An import refuses to overwrite edits made here: export the database into the package first (tools/catalog/prod_catalog.sh export <customer>).",
    "catalog.history.empty": "No import or edit yet.",
    "catalog.history.import": "{who} imported the package of {name}",
    "catalog.history.created.recipe": "{who} created the recipe {name}",
    "catalog.history.created.ingredient": "{who} created the ingredient {name}",
    "catalog.history.created.product": "{who} created the product {name}",
    "catalog.history.updated.recipe": "{who} changed the recipe {name}",
    "catalog.history.updated.ingredient": "{who} changed the ingredient {name}",
    "catalog.history.updated.product": "{who} changed the product {name}",
    "catalog.history.importPart.recipes": "recipes: {created} new, {updated} changed",
    "catalog.history.importPart.ingredients": "ingredients: {created} new, {updated} changed",
    "catalog.history.importPart.products": "products: {created} new, {updated} changed",
    "catalog.history.importNothing": "nothing changed",
    "catalog.history.overwritten": "Overwrote edits made in the admin: {ids}",
    "catalog.history.field.name": "name",
    "catalog.history.field.aliases": "names",
    "catalog.history.field.detector_classes": "detector classes",
    "catalog.history.field.kind": "kind",
    "catalog.history.field.recipe_id": "recipe",
  },
  cs: {
    "catalog.title": "Katalog",
    "catalog.description":
      "Recepty, suroviny a produkty, podle kterých vyhodnocení kontroluje pizzy. Jsou uložené v databázi tohoto zákazníka. Uložená změna platí pro další vyhodnocení; už vyhodnocené pizzy si výsledek ponechají.",
    "catalog.forbidden": "Katalog je dostupný jen roli vývojáře.",
    "catalog.reload": "Načíst znovu",
    "catalog.lastImport": "Poslední import balíčku: {who}",
    "catalog.neverImported": "Zatím nebyl importován žádný balíček.",
    "catalog.sourceFiles":
      "Tento backend vyhodnocuje podle balíčku katalogu z repozitáře (catalog_source=files). Změny se tu uloží do databáze, ale vyhodnocení je použije až s catalog_source=db.",
    "catalog.empty":
      "Katalog je prázdný. Nejdřív importujte balíček zákazníka: tools/catalog/prod_catalog.sh import <zákazník>.",
    "catalog.loading": "Načítání…",
    "catalog.error.unknown": "Neznámá chyba",

    "catalog.tab.recipes": "Recepty",
    "catalog.tab.ingredients": "Suroviny",
    "catalog.tab.products": "Produkty",
    "catalog.tab.history": "Historie",

    "catalog.chip.detectable": "Kontroluje se na fotce: detektor ji vidí jako {classes}",
    "catalog.chip.notDetectable": "Nekontroluje se: detektor ji nevidí",
    "catalog.chip.afterBaking": "po upečení",

    "catalog.save.conflict": "Mezitím to změnil někdo jiný ({error}). Načtěte aktuální stav, zkontrolujte změnu a uložte znovu.",
    "catalog.save.invalid": "Změna by katalog rozbila:",
    "catalog.save.failed": "Uložení selhalo: {error}",
    "catalog.save.reload": "Načíst aktuální",
    "catalog.save.cancel": "Zrušit",
    "catalog.save.saving": "Ukládám…",
    "catalog.save.save": "Uložit",

    "catalog.tags.remove": "Odebrat {value}",
    "catalog.tags.add": "Přidat",

    "catalog.picker.search": "Hledat surovinu…",
    "catalog.picker.none": "Žádná surovina neodpovídá.",
    "catalog.picker.taken": "v receptu",
    "catalog.picker.notDetectable": "nekontroluje se",

    "catalog.list.changedBy": "změnil(a) {who}",
    "catalog.list.empty": "Nic neodpovídá.",
    "catalog.legend.checkedSample": "šunka",
    "catalog.legend.checked": "kontroluje se na fotce",
    "catalog.legend.notCheckedSample": "mozzarella",
    "catalog.legend.notChecked": "nekontroluje se (detektor ji nevidí)",

    // recipes
    "catalog.recipes.search": "Hledat podle názvu, ID nebo suroviny…",
    "catalog.recipes.add": "Nový recept",
    "catalog.recipes.checked.one": "kontroluje se {count} z {total}",
    "catalog.recipes.checked.few": "kontrolují se {count} z {total}",
    "catalog.recipes.checked.other": "kontroluje se {count} z {total}",
    "catalog.recipe.newTitle": "Nový recept",
    "catalog.recipe.editTitle": "Recept {name}",
    "catalog.recipe.newDescription":
      "Podle ID vyhodnocení recept najde: u Vosime je to číslo z menu v názvu položky objednávky („40. Spooky Pizza“ → 40).",
    "catalog.recipe.editDescription": "ID {id}, verze {version}. Uložením vznikne nová verze.",
    "catalog.recipe.name": "Název",
    "catalog.recipe.id": "ID",
    "catalog.recipe.idMissing": "Vyplňte ID.",
    "catalog.recipe.idInvalid": "Jen písmena, číslice, „-“ nebo „_“.",
    "catalog.recipe.idTaken": "Toto ID už existuje.",
    "catalog.recipe.components": "Suroviny",
    "catalog.recipe.afterBakingColumn": "Po upečení",
    "catalog.recipe.pickIngredient": "Vyberte surovinu",
    "catalog.recipe.afterBaking": "po upečení",
    "catalog.recipe.removeComponent": "Odebrat surovinu",
    "catalog.recipe.addComponent": "Přidat surovinu",
    "catalog.recipe.duplicate": "Surovina je v receptu dvakrát.",
    "catalog.recipe.detectableHint":
      "Na fotce se kontrolují jen suroviny s okem (detektor je vidí); ostatní jsou v receptu pro úplnost. „Po upečení“ označuje, co se dává na upečenou pizzu.",
    "catalog.recipe.appliesHint": "Platí od dalšího vyhodnocení.",
    "catalog.recipe.extraIgnore": "Nikdy nezobrazovat jako suroviny navíc",
    "catalog.recipe.extraIgnoreAdd": "Přidat podobnou surovinu…",
    "catalog.recipe.extraIgnoreHint":
      "Za co detektor považuje některou ze surovin této pizzy (Spooky: uhlíkový čedar vypadá jako jalapeño). Pizza je nikdy neukáže jako suroviny navíc.",
    "catalog.recipe.extraIgnoreChanged": "nezobrazovat navíc: {names}",
    "catalog.recipe.saved": "{name} uloženo (verze {version})",
    "catalog.recipe.versions.one": "{count} verze",
    "catalog.recipe.versions.few": "{count} verze",
    "catalog.recipe.versions.other": "{count} verzí",
    "catalog.recipe.version": "v{version}",
    "catalog.recipe.validFrom": "od {at}",
    "catalog.recipe.validAlways": "od začátku",
    "catalog.recipe.renamed": "přejmenováno {from} → {to}",
    "catalog.recipe.orderOnly": "změnilo se pořadí surovin",

    // ingredients
    "catalog.ingredients.search": "Hledat podle názvu, ID nebo třídy…",
    "catalog.ingredients.add": "Nová surovina",
    "catalog.ingredients.filter": "Zobrazit",
    "catalog.ingredients.filter.all": "Všechny",
    "catalog.ingredients.filter.checked": "Kontrolované",
    "catalog.ingredients.filter.unchecked": "Nekontrolované",
    "catalog.ingredient.newTitle": "Nová surovina",
    "catalog.ingredient.editTitle": "Surovina {name}",
    "catalog.ingredient.newDescription": "Něco, co uvádějí recepty a objednávky; na fotce se kontroluje, když má třídy detektoru.",
    "catalog.ingredient.usage.one": "Používá ji {count} recept",
    "catalog.ingredient.usage.few": "Používají ji {count} recepty",
    "catalog.ingredient.usage.other": "Používá ji {count} receptů",
    "catalog.ingredient.name": "Název",
    "catalog.ingredient.nameHint": "Název, pod kterým ji vyhodnocení hlásí, třeba jako chybějící surovinu.",
    "catalog.ingredient.id": "ID",
    "catalog.ingredient.idMissing": "Vyplňte ID.",
    "catalog.ingredient.idInvalid": "Malá písmena a číslice oddělené „-“.",
    "catalog.ingredient.idTaken": "Toto ID už existuje.",
    "catalog.ingredient.aliases": "Názvy v receptech a objednávkách",
    "catalog.ingredient.aliasesHint":
      "Všechny názvy, pod kterými surovina chodí, včetně vlastního názvu a přídavků z pokladny. Na velikosti písmen a diakritice nezáleží; jeden název patří jen jedné surovině.",
    "catalog.ingredient.aliasPlaceholder": "Přidat název, Enter",
    "catalog.ingredient.aliasTaken": "„{alias}“ už je názvem suroviny {owner}.",
    "catalog.ingredient.classes": "Třídy detektoru",
    "catalog.ingredient.classesHint": "Třídy detektoru surovin, které tuto surovinu vidí. Žádná = na fotce se nekontroluje.",
    "catalog.ingredient.classPlaceholder": "Přidat třídu, Enter",
    "catalog.ingredient.unknownClasses":
      "Třídu {classes} nepoužívá žádná jiná surovina. Ověřte, že ji detektor zná, jinak se surovina bude vždy hlásit jako chybějící.",
    "catalog.ingredient.newHint": "Nová surovina se hledá až po stávajících.",
    "catalog.ingredient.extraMinCount": "Kusů pro zobrazení jako surovina navíc",
    "catalog.ingredient.extraMinCountHint":
      "Kolik jistých kusů musí detektor najít na pizze bez této suroviny, aby ji ukázal jako surovinu navíc. Prázdné = nastavení na stránce Vyhodnocení. Méně u velkých kusů (camembert: 2).",
    "catalog.ingredient.extraMinCountInvalid": "Celé číslo od 1 do 50.",
    "catalog.ingredient.extraMinShare": "Podíl pizzy pro zobrazení jako omáčka navíc",
    "catalog.ingredient.extraMinShareHint":
      "Omáčka se ukáže navíc, když pokryje aspoň tento podíl pizzy, která ji nemá mít. Prázdné = jakékoli množství, které detektor najde.",
    "catalog.ingredient.extraMinShareInvalid": "Od 0 do 100 %.",
    "catalog.ingredient.extraDefault": "z nastavení",
    "catalog.ingredient.extraAny": "jakýkoli",
    "catalog.ingredient.notChecked": "nekontroluje se",
    "catalog.ingredient.alsoCalled": "také: {names}",
    "catalog.ingredient.saved": "{name} uloženo",

    // products
    "catalog.products.note":
      "Vyhodnocení produkty zatím nepoužívá: recept pro Vosime se bere z čísla v názvu položky objednávky. Produkty budou přiřazovat recepty položkám z pokladen dalších zákazníků.",
    "catalog.products.search": "Hledat podle názvu, ID nebo receptu…",
    "catalog.products.add": "Nový produkt",
    "catalog.products.checkedAs": "kontroluje se jako {recipe}",
    "catalog.product.newTitle": "Nový produkt",
    "catalog.product.editTitle": "Produkt {name}",
    "catalog.product.description": "Co kuchyně prodává; pizza určuje recept, podle kterého se kontroluje.",
    "catalog.product.name": "Název",
    "catalog.product.id": "ID",
    "catalog.product.idMissing": "Vyplňte ID.",
    "catalog.product.idInvalid": "Jen písmena, číslice, „-“ nebo „_“.",
    "catalog.product.idTaken": "Toto ID už existuje.",
    "catalog.product.kind": "Druh",
    "catalog.product.kind.pizza": "Pizza",
    "catalog.product.kind.other": "Jiné",
    "catalog.product.recipe": "Recept",
    "catalog.product.recipeMissing": "Pizza potřebuje recept.",
    "catalog.product.noRecipe": "Bez receptu",
    "catalog.product.saved": "{name} uloženo",

    // history
    "catalog.history.note":
      "Importy balíčku zákazníka a úpravy udělané tady. Import odmítne přepsat úpravy z adminu: nejdřív databázi vyexportujte do balíčku (tools/catalog/prod_catalog.sh export <zákazník>).",
    "catalog.history.empty": "Zatím žádný import ani úprava.",
    "catalog.history.import": "{who} importoval(a) balíček {name}",
    "catalog.history.created.recipe": "{who} vytvořil(a) recept {name}",
    "catalog.history.created.ingredient": "{who} vytvořil(a) surovinu {name}",
    "catalog.history.created.product": "{who} vytvořil(a) produkt {name}",
    "catalog.history.updated.recipe": "{who} změnil(a) recept {name}",
    "catalog.history.updated.ingredient": "{who} změnil(a) surovinu {name}",
    "catalog.history.updated.product": "{who} změnil(a) produkt {name}",
    "catalog.history.importPart.recipes": "recepty: {created} nových, {updated} změněných",
    "catalog.history.importPart.ingredients": "suroviny: {created} nových, {updated} změněných",
    "catalog.history.importPart.products": "produkty: {created} nových, {updated} změněných",
    "catalog.history.importNothing": "beze změny",
    "catalog.history.overwritten": "Přepsal(a) úpravy z adminu: {ids}",
    "catalog.history.field.name": "název",
    "catalog.history.field.aliases": "názvy",
    "catalog.history.field.detector_classes": "třídy detektoru",
    "catalog.history.field.kind": "druh",
    "catalog.history.field.recipe_id": "recept",
  },
});
